import { useEffect, useMemo, useRef, useState } from 'react'
import type { PracticeItem } from '@domain/events.ts'
import type { Member } from '@domain/state.ts'
import {
  PREGUNTAS_POR_SESION,
  acierta,
  balance,
  cursoPorEdad,
  destrezasDe,
  familiasDe,
  loQueCuesta,
  intentoDe,
  siguienteEjercicio,
  type Destreza,
  type Ejercicio,
  type Familia,
  type Intento,
} from '@domain/rules/mates.ts'
import { ayudaPara, preguntasDe, trazar, type Traza } from '@domain/rules/cuentas.ts'
import { pedirExplicacion, type Explicacion } from '@adapters/ai/explicar.ts'
import { assistantAvailable, assistantConfig } from '@adapters/ai/config.ts'
import { PasoAPaso, type ResultadoPasos } from './PasoAPaso.tsx'
import { Teclado } from './Teclado.tsx'
import { es } from '../strings/es.ts'
import { textoDelPaso } from '../strings/pasos.ts'

/**
 * El repaso de mates.
 *
 * Empieza eligiendo qué repasar —sumas, restas, multiplicaciones, divisiones o
 * de todo— y después pregunta doce cuentas. Lo que hace de esto algo más que
 * una app de cuentas es cómo ayuda:
 *
 * - **Sin historial, se prueba.** La primera vez que sale una destreza no hay
 *   pasos ni ejemplo: si sale bien, no había nada que enseñar.
 * - **Tras un fallo, se ve hecha y se hace una parecida.** El ejemplo
 *   resuelto y, justo después, un problema gemelo del que el niño hace solo el
 *   último paso. Con cada acierto, un paso más, hasta hacerla entera; y
 *   entonces vuelve a preguntarse sin pasos. Es el desvanecimiento hacia atrás
 *   de Renkl y Atkinson (2003), y está en `ayudaPara`.
 * - **La ayuda está siempre a un botón.** «Hacerla paso a paso» antes de
 *   contestar, y «ver cómo se hace» después de fallar.
 *
 * Dos decisiones de antes que siguen en pie, las dos por los niños de esta
 * casa: **no hay reloj a la vista** —el tiempo se mide, no se enseña— y **un
 * fallo se contesta con el resultado y se pasa**, sin ironía ni «casi».
 */

type Fase =
  | { readonly name: 'elegir' }
  | { readonly name: 'ejemplo' }
  | { readonly name: 'guiado'; readonly pasosQueHace: number }
  | { readonly name: 'pregunta' }
  | {
      readonly name: 'veredicto'
      readonly ok: boolean
      /** Si la hizo paso a paso, cómo fue. */
      readonly conPasos?: ResultadoPasos
      /** Ya ha visto cómo se hace, tras fallarla. */
      readonly repasado?: boolean
    }
  | { readonly name: 'repaso' }
  | { readonly name: 'fin' }

const FAMILIAS: readonly (Familia | 'todo')[] = [
  'suma',
  'resta',
  'multiplicacion',
  'division',
  'todo',
]

export function Mates({
  member,
  historial,
  mision,
  draw,
  onFinish,
  onBack,
}: {
  member: Member
  /** Lo que ya ha contestado otros días, sacado del registro. */
  historial: readonly Intento[]
  /**
   * La misión del día, si el padre ha puesto una. Se mira al entrar y no al
   * salir: así el final puede decir «hoy ya estaba hecha» sin tener que leer el
   * estado justo después de haberlo cambiado.
   */
  mision:
    { readonly coins: number; readonly palabra: string; readonly yaEstaba: boolean } | undefined
  /** El azar de la casa, el mismo que escribe en el registro. */
  draw: () => number
  /** Devuelve lo que la sesión ha dado de sí; la pantalla no escribe en el log. */
  onFinish: (items: readonly PracticeItem[]) => void
  onBack: () => void
}) {
  const edad =
    member.birthYear === undefined ? undefined : new Date().getFullYear() - member.birthYear
  const curso = cursoPorEdad(edad)
  const familias = useMemo(() => familiasDe(curso), [curso])
  // Sin servidor detrás no hay explicación, y entonces el botón no existe: mejor
  // que enseñarlo y que al pulsarlo no pase nada.
  const puedeExplicar = assistantAvailable()

  const [familia, setFamilia] = useState<Familia | 'todo' | undefined>(undefined)
  const destrezas = useMemo(
    () => destrezasDe(curso, familia === 'todo' ? undefined : familia),
    [curso, familia],
  )
  const [hechas, setHechas] = useState<readonly PracticeItem[]>([])
  const [fase, setFase] = useState<Fase>({ name: 'elegir' })
  const [escrito, setEscrito] = useState('')
  const [explicacion, setExplicacion] = useState<Explicacion | null>(null)
  const [explicando, setExplicando] = useState(false)
  const [sinExplicacion, setSinExplicacion] = useState(false)
  const [ejercicio, setEjercicio] = useState<Ejercicio | undefined>(undefined)
  const traza: Traza | undefined = useMemo(
    () => (ejercicio ? trazar(ejercicio) : undefined),
    [ejercicio],
  )

  // El cronómetro arranca cuando la pregunta se pinta, no cuando se decide: es
  // lo que de verdad tardó en contestar. Va en un efecto porque leer el reloj
  // mientras se renderiza no está permitido, y con razón.
  const empezada = useRef(0)
  useEffect(() => {
    empezada.current = Date.now()
  }, [ejercicio])

  // Lo de hoy pesa igual que lo de otros días para elegir la siguiente: si acaba
  // de fallar el 7×8, tiene que volver a salir en esta misma sesión.
  const todo: readonly Intento[] = [...historial, ...hechas.map(intentoDe)]

  /**
   * Elige la siguiente cuenta y con cuánta ayuda se presenta. Recibe la lista
   * de destrezas porque al elegir familia la del estado aún es la de antes.
   */
  const plantear = (lista: readonly Destreza[], evitar?: string): void => {
    const siguiente = siguienteEjercicio(lista, todo, curso, draw, evitar) ?? {
      destreza: 'tabla-2',
      pide: '2 × 2',
      solucion: '4',
    }
    setEjercicio(siguiente)
    const camino = trazar(siguiente)
    const ayuda = ayudaPara(siguiente.destreza, todo, camino ? preguntasDe(camino) : 0)
    if (camino && ayuda.ejemploAntes) setFase({ name: 'ejemplo' })
    else if (camino && ayuda.pasosQueHace !== 'todos') {
      setFase({ name: 'guiado', pasosQueHace: ayuda.pasosQueHace })
    } else setFase({ name: 'pregunta' })
  }

  const elegir = (cual: Familia | 'todo'): void => {
    setFamilia(cual)
    plantear(destrezasDe(curso, cual === 'todo' ? undefined : cual))
  }

  const anotar = (ok: boolean, dijo: string, ayuda: boolean): PracticeItem[] => {
    if (!ejercicio) return [...hechas]
    const item: PracticeItem = {
      destreza: ejercicio.destreza,
      pide: ejercicio.pide,
      dijo: dijo.slice(0, 20),
      ok,
      // Acotado como en el vocabulario: si deja la tablet en la mesa y vuelve
      // media hora después, eso no es lo que tardó en contestar.
      ms: Math.min(600_000, Date.now() - empezada.current),
      ...(ayuda ? { ayuda: true } : {}),
    }
    const lista = [...hechas, item]
    setHechas(lista)
    return lista
  }

  const responder = (): void => {
    if (escrito === '' || !ejercicio) return
    const ok = acierta(ejercicio, escrito)
    anotar(ok, escrito, false)
    setFase({ name: 'veredicto', ok })
  }

  /** Tras el ejemplo, una parecida: misma destreza, otros números. */
  const gemela = (): void => {
    if (!ejercicio) return
    const destreza = destrezas.find((d) => d.id === ejercicio.destreza)
    const otra = destreza?.generar(draw) ?? ejercicio
    setEjercicio(otra)
    setFase({ name: 'guiado', pasosQueHace: 1 })
  }

  const acabarPasos = (resultado: ResultadoPasos): void => {
    if (!ejercicio) return
    // Con las pistas se puede fallar y seguir; lo que no se sabe es lo que
    // hubo que decirle. Eso es lo que cuenta como no sabido.
    const ok = resultado.dichos === 0
    anotar(ok, ok ? ejercicio.solucion : '', true)
    setFase({ name: 'veredicto', ok, conPasos: resultado })
  }

  const explicar = async (): Promise<void> => {
    if (!ejercicio) return
    setExplicando(true)
    setSinExplicacion(false)
    try {
      const pasos = traza ? traza.pasos.map((p) => textoDelPaso(p).explica) : []
      const hecho = await pedirExplicacion(assistantConfig(), ejercicio, curso, pasos)
      if (hecho.ok) setExplicacion(hecho.explicacion)
      else setSinExplicacion(true)
    } finally {
      setExplicando(false)
    }
  }

  const seguir = (): void => {
    setEscrito('')
    setExplicacion(null)
    setSinExplicacion(false)
    if (hechas.length >= PREGUNTAS_POR_SESION) {
      setFase({ name: 'fin' })
      onFinish(hechas)
      return
    }
    plantear(destrezas, ejercicio?.destreza)
  }

  const dejarlo = (): void => {
    if (hechas.length > 0) onFinish(hechas)
    onBack()
  }

  if (fase.name === 'elegir') {
    return (
      <div className="mx-auto max-w-xl px-5 pt-6 pb-16">
        <h1 className="mt-0 mb-1 text-xl font-bold">{es.mates.title}</h1>
        <p className="mt-0 mb-6 text-2xl font-extrabold">{es.mates.elegirTitulo}</p>
        <div className="grid gap-3">
          {FAMILIAS.filter((f) => f === 'todo' || familias.includes(f)).map((f) => (
            <button
              key={f}
              type="button"
              className="border-rule bg-surface min-h-16 rounded-xl border px-6 text-left text-xl font-bold"
              onClick={() => {
                elegir(f)
              }}
            >
              {es.mates.familia[f]}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="text-muted mt-8 w-full text-sm font-bold underline underline-offset-4"
          onClick={onBack}
        >
          {es.mates.dejarlo}
        </button>
      </div>
    )
  }

  if (fase.name === 'fin') {
    const cuenta = balance(hechas.map(intentoDe), todo)
    const cuesta = loQueCuesta(todo, 2)
    return (
      <div className="mx-auto max-w-xl px-5 pt-8 pb-16">
        <h1 className="mt-0 mb-4 text-3xl font-extrabold">{es.mates.finTitulo}</h1>
        <p className="m-0 text-xl font-bold">
          {es.mates.finCuenta(cuenta.aciertos, hechas.length)}
        </p>
        {mision === undefined ? null : mision.yaEstaba ? (
          <p className="text-muted m-0 mt-2">{es.mates.finYaEstaba}</p>
        ) : (
          <p className="text-mint m-0 mt-2 text-lg font-bold">
            {es.mates.finGanado(mision.coins, mision.palabra)}
          </p>
        )}
        {cuenta.conAyuda > 0 ? (
          <p className="text-muted m-0 mt-2">{es.mates.finConAyuda(cuenta.conAyuda)}</p>
        ) : null}
        {cuenta.lentos > 0 ? (
          <p className="text-muted m-0 mt-2">{es.mates.finLentas(cuenta.lentos)}</p>
        ) : null}
        {cuesta.length > 0 ? (
          <p className="m-0 mt-2">
            {es.mates.finPractica(cuesta.map((f) => f.titulo).join(' y '))}
          </p>
        ) : null}
        <div className="mt-8 flex flex-wrap gap-3">
          <button
            type="button"
            className="bg-teal min-h-14 rounded-xl px-6 text-lg font-bold text-white"
            onClick={onBack}
          >
            {es.mates.terminar}
          </button>
        </div>
      </div>
    )
  }

  if (!ejercicio) return null

  const cabecera = (
    <div className="mb-6 flex items-baseline justify-between gap-4">
      <h1 className="m-0 text-xl font-bold">{es.mates.title}</h1>
      <p className="text-muted m-0 text-sm font-bold">
        {es.mates.vaPor(Math.min(hechas.length + 1, PREGUNTAS_POR_SESION), PREGUNTAS_POR_SESION)}
      </p>
    </div>
  )

  const pie = (
    <button
      type="button"
      className="text-muted mt-8 w-full text-sm font-bold underline underline-offset-4"
      onClick={dejarlo}
    >
      {es.mates.dejarlo}
    </button>
  )

  if ((fase.name === 'ejemplo' || fase.name === 'repaso') && traza) {
    const esRepaso = fase.name === 'repaso'
    return (
      <div className="mx-auto max-w-xl px-5 pt-6 pb-16">
        {cabecera}
        {esRepaso ? null : <p className="text-muted m-0 mb-4">{es.mates.ejemploLuego}</p>}
        <PasoAPaso
          key={`${fase.name}:${ejercicio.pide}`}
          traza={traza}
          pide={ejercicio.pide}
          modo="ejemplo"
          pasosQueHace={0}
          color={member.accent}
          titulo={es.mates.ejemploTitulo}
          botonFinal={esRepaso ? es.mates.siguiente : es.mates.ahoraTu}
          onFin={() => {
            // Tras el repaso se vuelve al veredicto: desde ahí se puede pedir
            // que lo cuenten con otras palabras, o seguir.
            if (esRepaso) setFase({ name: 'veredicto', ok: false, repasado: true })
            else gemela()
          }}
        />
        {pie}
      </div>
    )
  }

  if (fase.name === 'guiado' && traza) {
    return (
      <div className="mx-auto max-w-xl px-5 pt-6 pb-16">
        {cabecera}
        <PasoAPaso
          key={`guiado:${ejercicio.pide}`}
          traza={traza}
          pide={ejercicio.pide}
          modo="guiado"
          pasosQueHace={fase.pasosQueHace}
          color={member.accent}
          titulo={es.mates.teToca}
          botonFinal={es.mates.siguiente}
          onFin={acabarPasos}
        />
        {pie}
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-xl px-5 pt-6 pb-16">
      {cabecera}

      <p
        data-testid="enunciado"
        className="border-rule bg-surface m-0 rounded-2xl border p-8 text-center text-5xl font-extrabold tabular-nums"
        style={{ color: member.accent }}
      >
        {ejercicio.pide}
      </p>

      {fase.name === 'veredicto' ? (
        <div role="status" className="mt-6 text-center">
          {fase.conPasos ? (
            <p className="m-0 text-2xl font-extrabold">
              {fase.conPasos.dichos === 0
                ? es.mates.hechaConPasosBien
                : es.mates.hechaConPasosFallos(fase.conPasos.dichos)}
            </p>
          ) : (
            <p className="m-0 text-2xl font-extrabold">
              {fase.ok ? es.mates.correcto : es.mates.noEsEsa}
            </p>
          )}
          {fase.ok && !fase.conPasos ? null : (
            <p className="m-0 mt-2 text-xl font-bold tabular-nums">
              {es.mates.esta(ejercicio.pide, ejercicio.solucion)}
            </p>
          )}

          {!fase.ok && !fase.conPasos && !fase.repasado && traza ? (
            <button
              type="button"
              className="border-rule mt-4 min-h-14 w-full rounded-xl border px-6 font-bold"
              onClick={() => {
                setFase({ name: 'repaso' })
              }}
            >
              {es.mates.verComoSeHace}
            </button>
          ) : null}

          {/* Con otras palabras solo tiene sentido después de haber visto los
              pasos; sin pasos que enseñar, es la única explicación que hay. */}
          {fase.ok ||
          !puedeExplicar ||
          (traza && !fase.repasado && !fase.conPasos) ? null : explicacion ? (
            <div className="border-rule bg-surface mt-6 rounded-xl border p-4 text-left">
              <p className="text-muted m-0 mb-2 text-xs font-bold tracking-[0.1em] uppercase">
                {es.mates.explicacionTitulo}
              </p>
              <ol className="m-0 grid list-decimal gap-2 pl-5">
                {explicacion.pasos.map((paso) => (
                  <li key={paso} className="m-0 text-base">
                    {paso}
                  </li>
                ))}
              </ol>
            </div>
          ) : (
            <button
              type="button"
              className="border-rule mt-4 min-h-14 w-full rounded-xl border px-6 font-bold disabled:opacity-60"
              disabled={explicando}
              onClick={() => {
                void explicar()
              }}
            >
              {explicando ? es.mates.explicando : es.mates.explicar}
            </button>
          )}
          {sinExplicacion ? (
            <p className="text-muted m-0 mt-3 text-sm">{es.mates.sinExplicacion}</p>
          ) : null}

          <button
            type="button"
            className="bg-teal mt-6 min-h-14 w-full rounded-xl px-6 text-lg font-bold text-white"
            onClick={seguir}
          >
            {es.mates.siguiente}
          </button>
        </div>
      ) : (
        <>
          <Teclado escrito={escrito} onEscrito={setEscrito} onComprobar={responder} />
          {traza ? (
            <button
              type="button"
              className="border-rule mt-3 min-h-12 w-full rounded-xl border px-4 font-bold"
              onClick={() => {
                setFase({ name: 'guiado', pasosQueHace: preguntasDe(traza) })
              }}
            >
              {es.mates.ayuda}
            </button>
          ) : null}
        </>
      )}

      {pie}
    </div>
  )
}
